import React, { useMemo } from 'react';
import {
    Box,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { shellTokens } from '../../shell/theme/shellTheme';
import {
    filterPositionsByInstance,
    normalizePositions,
    sortPositions,
} from '../positionMappers';
import PositionRow from './PositionRow';

const LedgerSection = ({ title, count, positions }) => {
    if (!positions.length) {
        return null;
    }

    return (
        <Box sx={{ mb: positions.length ? 2 : 0 }}>
            <Typography
                sx={{
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: shellTokens.muted,
                    mb: 1,
                }}
            >
                {title} ({count})
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>Strategy</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>Client</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>Symbol</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>Entry</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>Exit</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>Net P&amp;L</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>Params</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem' }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {positions.map((position) => (
                            <PositionRow key={position.positionId} position={position} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const PositionsLedger = ({ positions = [], instances = [], selectedInstanceId, loading }) => {
    const labelMap = useMemo(() => {
        const map = new Map();
        instances.forEach((instance) => {
            map.set(`${instance.strategyId}:${instance.clientId}`, {
                strategyLabel: instance.strategyLabel || instance.strategyId,
                clientAlias: instance.clientAlias || instance.clientId,
            });
        });
        return map;
    }, [instances]);

    const { openPositions, closedPositions } = useMemo(() => {
        const filtered = filterPositionsByInstance(positions, selectedInstanceId);
        const normalized = sortPositions(normalizePositions(filtered, labelMap));
        return {
            openPositions: normalized.filter((position) => position.status === 'open'),
            closedPositions: normalized.filter((position) => position.status === 'closed'),
        };
    }, [positions, selectedInstanceId, labelMap]);

    const totalCount = openPositions.length + closedPositions.length;
    const selectionLabel = useMemo(() => {
        if (!selectedInstanceId) {
            return 'All instances';
        }
        const labels = labelMap.get(selectedInstanceId);
        if (labels) {
            return `${labels.strategyLabel} × ${labels.clientAlias}`;
        }
        return selectedInstanceId.replace(':', ' × ');
    }, [selectedInstanceId, labelMap]);

    return (
        <Box sx={{ bgcolor: shellTokens.surface, border: `1px solid ${shellTokens.border}`, borderRadius: `${shellTokens.radius}px`, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${shellTokens.border}` }}>
                <Typography
                    sx={{
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: shellTokens.muted,
                    }}
                >
                    Positions ledger
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', mt: 0.25 }}>
                    {selectionLabel}
                </Typography>
                {selectedInstanceId ? (
                    <Typography sx={{ fontSize: '0.74rem', color: shellTokens.muted, mt: 0.25 }}>
                        Filtered to selected matrix cell
                    </Typography>
                ) : null}
            </Box>

            <Box sx={{ p: 2 }}>
                {loading ? (
                    <Skeleton variant="rounded" height={180} />
                ) : totalCount === 0 ? (
                    <Typography sx={{ color: shellTokens.muted, fontSize: '0.84rem' }}>
                        {selectedInstanceId
                            ? 'No positions for the selected instance.'
                            : 'No positions returned from the fleet API.'}
                    </Typography>
                ) : (
                    <>
                        <LedgerSection title="Open" count={openPositions.length} positions={openPositions} />
                        <LedgerSection title="Closed" count={closedPositions.length} positions={closedPositions} />
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PositionsLedger;
